import { Select, Stack, Text } from '@mantine/core';
import { ContactPoint, Patient } from '@medplum/fhirtypes';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { CONTACTPOINT_COMMENT_STRUCTURE_DEFINITION_EXTENSION } from '@bariendo/lib/extensions';

function getPatientPhoneNumbers(patient: Patient | null): ContactPoint[] {
  if (!patient?.telecom) return [];
  return patient.telecom.filter((t) => t.system === 'phone' && t.value);
}

function getPhoneLabel(phone: ContactPoint): string {
  return (
    phone.extension?.find((ext) => ext.url === CONTACTPOINT_COMMENT_STRUCTURE_DEFINITION_EXTENSION)?.valueString ?? ''
  );
}

interface PhoneSelectItemProps {
  phoneNumber: string;
  label: string;
}

function PhoneSelectItem({ phoneNumber, label }: PhoneSelectItemProps) {
  return (
    <Stack gap={2}>
      <Text size="sm" fw={500}>
        {formatPhoneNumber(phoneNumber)}
      </Text>
      {label && (
        <Text size="xs" c="dimmed">
          {label}
        </Text>
      )}
    </Stack>
  );
}

export interface PatientPhoneSelectorProps {
  patient: Patient | null;
  value: string;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

export default function PatientPhoneSelector({ patient, value, onChange, disabled }: PatientPhoneSelectorProps) {
  const phoneNumbers = getPatientPhoneNumbers(patient);
  const hasPhoneNumbers = phoneNumbers.length > 0;

  const data = phoneNumbers.map((phone) => ({
    value: phone.value!,
    label: formatPhoneNumber(phone.value),
    phoneNumber: phone.value!,
    phoneLabel: getPhoneLabel(phone),
  }));

  const placeholder = hasPhoneNumbers
    ? 'Select phone number'
    : patient
      ? 'No phone numbers available'
      : 'Select a guest';

  return (
    <Select
      data={data}
      value={value || null}
      onChange={onChange}
      disabled={disabled || !patient}
      placeholder={placeholder}
      searchable
      nothingFoundMessage="No phone numbers found"
      renderOption={({ option }) => {
        const phoneData = data.find((d) => d.value === option.value);
        return <PhoneSelectItem phoneNumber={phoneData?.phoneNumber || ''} label={phoneData?.phoneLabel || ''} />;
      }}
    />
  );
}
